import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Flex, Input, Typography, theme } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LeftOutlined,
  Loading3QuartersOutlined,
  WarningFilled,
} from '@ant-design/icons';

import { CfReconnectReason } from '../api/cloudflareFlowApi';
import type { CfNexusOpening } from '../cfNexusOpening';
import type { CfNexusFromStep } from '../cfStepTrail';
import { reportOnActivation } from '../ctaActivation';
import { useCfFlowEventTracker } from '../useCfFlowEventTracker';
import {
  CfTokenRejection,
  ConnectCfTokenError,
  ConnectCfTokenResponse,
  isCfTokenRejectionReason,
  useConnectCfTokenMutation,
} from '../api/cloudflareTokenApi';
import CopyableValue from './CopyableValue';
import { BETA_CLOSED_REASON, BETA_CLOSED_TITLE } from './betaClosedCopy';
import { TOKEN_STEP_LABEL, stuckPrompt } from '../nexus/buildNexusSummary';
import {
  CfTokenFieldStatus,
  ConnectProblem,
  deriveCfTokenFieldStatus,
  errorShownOnTokenField,
  nexusErrorTextOf,
} from './cfTokenFieldStatus';
import { useCfTokenCheck } from './useCfTokenCheck';
import CloudflareStepHeader from './CloudflareStepHeader';

const { Text, Link } = Typography;

export const CF_TOKEN_EVENTS = {
  createClicked: 'Automated CF Integration: Token Create Clicked',
} as const;

/**
 * The version of the acknowledgement text below, stored with the record.
 *
 * It must match a value the server knows (PRE-3281): the endpoint refuses a
 * version it does not recognise, so a change to the wording here without a
 * matching change there fails loudly rather than storing a record that names a
 * disclosure nobody was shown.
 */
export const ACKNOWLEDGEMENT_VERSION = 'cf-token-disclosure-v1';

export const ACKNOWLEDGEMENT_TEXT =
  'I understand that I am responsible for the lifecycle and scope of this token and that Prerender cannot revoke the token on my behalf.';

const SCOPE_GUIDE_URL = 'https://developers.cloudflare.com/fundamentals/api/get-started/create-token/';

/** A 429 (the route allows 10/min), a 500 or a dropped connection. */
const REQUEST_FAILED_COPY =
  "We couldn't submit your token just now. Nothing was saved. Paste it again and try once more.";

/**
 * A 409 is either a setup that already holds the flow or a flow that has ended.
 * Only the status is read, so the copy has to be true of both.
 */
const flowNotTakingTokenCopy = (domain: string) => ({
  message: `Prerender can't take a token for ${domain} right now.`,
  description: 'A setup may already be running, or this one has ended. Reload the page to see where it stands.',
});

/**
 * Why the customer is back on this screen (PRE-3528). The deletion message
 * agrees with the retention notice of PRE-3341, and the retention period is the
 * server's: a number kept here could not be checked against anything.
 */
type ReconnectCopy = { type: 'warning' | 'error'; message: string; description: string };

const retentionSentence = (days: number | undefined): string =>
  days === undefined
    ? 'Our retention rule limits how long we keep a stored token, and that time ran out.'
    : `Our retention rule keeps a stored token for ${days} days at most, and that time ran out.`;

const RECONNECT_COPY: Record<
  CfReconnectReason,
  (domain: string, retentionDays?: number) => ReconnectCopy
> = {
  deleted_by_retention: (domain, retentionDays) => ({
    type: 'warning',
    message: `Prerender deleted the Cloudflare token for ${domain}`,
    description:
      `${retentionSentence(retentionDays)} ` +
      'You did nothing wrong. Your setup is saved where you left it. Create a new token to carry on. ' +
      'Deleting our copy did not revoke the old token: revoke it in Cloudflare if you no longer need it.',
  }),
  revoked: (domain) => ({
    type: 'error',
    message: 'Cloudflare no longer accepts your token',
    description:
      `The token you connected for ${domain} was revoked, expired, or lost the permissions this integration needs. ` +
      'Your setup is saved where you left it. Create a new token to carry on.',
  }),
};

export const TOKEN_LINK_UNAVAILABLE_MESSAGE =
  'The token link is not available yet';
export const TOKEN_LINK_UNAVAILABLE_DESCRIPTION =
  'Prerender builds this link with the exact permissions the install needs. Reload the page in a minute and it will be here. ' +
  'Creating a token without it risks one that is missing a permission, and Cloudflare cannot widen a token after it is made.';

const FIELD_STATUS: Record<CfTokenFieldStatus['kind'], 'warning' | 'error' | undefined> = {
  idle: undefined,
  checking: undefined,
  success: undefined,
  warning: 'warning',
  error: 'error',
};

const FieldStatusIcon = ({ kind }: { kind: CfTokenFieldStatus['kind'] }) => {
  const { token } = theme.useToken();

  switch (kind) {
    case 'idle':
      return null;
    case 'checking':
      return <Loading3QuartersOutlined spin />;
    case 'success':
      return <CheckCircleFilled style={{ color: token.colorSuccess }} />;
    case 'warning':
      return <WarningFilled style={{ color: token.colorWarning }} />;
    case 'error':
      return <CloseCircleFilled style={{ color: token.colorError }} />;
  }
};

export type CloudflareTokenStepProps = {
  domain: string;
  /**
   * Present when the customer is here to replace a token that is gone rather
   * than to connect a first one. Chooses the banner above the field; the
   * server decides the value.
   */
  reconnectReason?: CfReconnectReason;
  /** The retention period to quote, from the server. Only read on the deletion banner. */
  retentionDays?: number;
  /**
   * The Cloudflare zone the token has to be scoped to, which the customer must
   * find by name in a Cloudflare list. It is usually the registrable apex
   * rather than the domain itself, so it cannot be derived here without a public
   * suffix list; until a caller has the real name from Cloudflare, the domain is
   * the closest true thing to show.
   */
  zoneName?: string;
  /** From PRE-3280. Opens Cloudflare with the four permission groups filled in. */
  tokenTemplateUrl?: string;
  /** Server-side count, so a refresh cannot take the Nexus offer away. */
  initialAttemptNumber?: number;
  onConnected?: (outcome: ConnectCfTokenResponse) => void;
  onGoBack?: () => void;
  fromStep?: Extract<CfNexusFromStep, 'token' | 'reconnect'>;
  onOpenNexus?: (opening: CfNexusOpening) => void;
  onErrorShownChange?: (errorShown: string | null) => void;
};

/**
 * Mirrors `NEXUS_ESCAPE_ATTEMPT` in rest-api's `ValidateCfTokenUseCase`. The
 * server counts only connects, so tokens the live check rejected are added here.
 */
export const NEXUS_ESCAPE_ATTEMPT = 2;

const problemOf = ({ status, rejection }: Partial<ConnectCfTokenError>): ConnectProblem => {
  if (rejection) return rejection.reason;
  // `CfBetaEntryGuard` refusing entry: asking for another paste would be refused every time.
  if (status === 403) return 'beta_closed';
  if (status === 409) return 'flow_not_taking_token';
  return 'request_failed';
};

/**
 * Where the customer hands us a credential (PRE-3291 / F3).
 *
 * The disclosure and the acknowledgement come from the DPO review of
 * 2026-08-13. The legal basis is Article 6(1)(b), so this is a **disclosure
 * with a recorded acknowledgement, not a consent gate**.
 */
const CloudflareTokenStep = ({
  domain,
  reconnectReason,
  retentionDays,
  zoneName,
  tokenTemplateUrl,
  initialAttemptNumber = 0,
  onConnected,
  onGoBack,
  fromStep = 'token',
  onOpenNexus,
  onErrorShownChange,
}: CloudflareTokenStepProps) => {
  const { token: designToken } = theme.useToken();
  const track = useCfFlowEventTracker();
  const [tokenValue, setTokenValue] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [latest, setLatest] = useState<ConnectCfTokenResponse | CfTokenRejection | null>(null);
  const [problem, setProblem] = useState<ConnectProblem | null>(null);
  const [connectToken, { isLoading }] = useConnectCfTokenMutation();
  const { checkState, betaClosed, rejectedChecks, retry } = useCfTokenCheck(domain, tokenValue);
  const fieldStatus = deriveCfTokenFieldStatus(checkState, problem, domain);
  const errorShown = errorShownOnTokenField(checkState, problem);
  const isChecked = checkState.phase === 'answered' || checkState.phase === 'unchecked';
  const canConnect = acknowledged && isChecked && !fieldStatus.blocking;

  const attemptNumber = latest?.attemptNumber ?? initialAttemptNumber;
  const offerNexus = latest?.offerNexus || attemptNumber + rejectedChecks >= NEXUS_ESCAPE_ATTEMPT;
  // The reason is the server's and its column has no enum type: a miss must not throw out of render.
  const reconnect = reconnectReason
    ? (RECONNECT_COPY[reconnectReason] ?? RECONNECT_COPY.revoked)(domain, retentionDays)
    : null;

  useEffect(() => {
    onErrorShownChange?.(errorShown);

    return () => onErrorShownChange?.(null);
  }, [errorShown, onErrorShownChange]);

  const onConnect = async () => {
    if (isLoading || !canConnect) return;

    setProblem(null);

    try {
      const response = await connectToken({
        domain,
        token: tokenValue.trim(),
        acknowledgementVersion: ACKNOWLEDGEMENT_VERSION,
      }).unwrap();

      setTokenValue('');
      setLatest(response);
      onConnected?.(response);
    } catch (error) {
      const failure = (error ?? {}) as Partial<ConnectCfTokenError>;
      if (failure.rejection) {
        setLatest(failure.rejection);
      } else {
        // Kept only for a verdict, which the customer fixes in the field. A value
        // left in state is a value that reaches a re-render, a devtools snapshot
        // or an error report.
        setTokenValue('');
      }
      setProblem(problemOf(failure));
    }
  };

  const onTokenChange = (value: string) => {
    setTokenValue(value);
    if (isCfTokenRejectionReason(problem)) setProblem(null);
  };

  const openNexus = () =>
    onOpenNexus?.({
      fromStep,
      domain,
      errorShown,
      entryPoint: 'verify_step',
      prefillText: stuckPrompt({
        domain,
        step: TOKEN_STEP_LABEL,
        error: nexusErrorTextOf(fieldStatus),
      }),
    });

  const onRetry = () => {
    if (isCfTokenRejectionReason(problem)) setProblem(null);
    retry();
  };

  const createTokenActivation = tokenTemplateUrl
    ? reportOnActivation(() => track(CF_TOKEN_EVENTS.createClicked, { from_step: fromStep }))
    : undefined;

  return (
    <Flex vertical gap={designToken.marginLG}>
      <CloudflareStepHeader
        title="Connect your Cloudflare account"
        description="Almost there. Create an API token so we can automatically set up your integration"
      />

      {reconnect && (
        <Alert
          type={reconnect.type}
          showIcon
          role="alert"
          message={reconnect.message}
          description={reconnect.description}
        />
      )}

      <Card>
        <Flex gap={designToken.marginXL} wrap="wrap">
          <Flex vertical gap={designToken.marginMD} flex={1} style={{ minWidth: designToken.screenXS }}>
            <Flex justify="center">
              <Button
                color="primary"
                variant="outlined"
                size="large"
                href={tokenTemplateUrl}
                disabled={!tokenTemplateUrl}
                // A new tab, so the customer keeps the wizard behind them and
                // does not lose their place going to Cloudflare and back.
                target="_blank"
                rel="noopener noreferrer"
                {...createTokenActivation}
              >
                Create Cloudflare token
              </Button>
            </Flex>

            {!tokenTemplateUrl && (
              <Alert
                type="warning"
                showIcon
                message={TOKEN_LINK_UNAVAILABLE_MESSAGE}
                description={TOKEN_LINK_UNAVAILABLE_DESCRIPTION}
              />
            )}

            <Card size="small" style={{ background: designToken.colorFillQuaternary }}>
              <Text strong>Activities checklist</Text>
              <ol style={{ marginBottom: 0, paddingLeft: designToken.marginLG }}>
                <li>Create an API token in Cloudflare using the link.</li>
                {/* "selected", not "pre-selected" — the DPO review asked for the
                    wording that describes what the customer will actually see. */}
                <li>Keep the selected permissions as is.</li>
                <li>
                  Recommended: restrict the token to just this domain. Choose Include, then Specific zone, then the zone
                  below.{' '}
                  <Link
                    href={SCOPE_GUIDE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: designToken.colorPrimary }}
                  >
                    Learn more
                  </Link>
                </li>
              </ol>

              <Flex align="center" gap={designToken.marginXS} wrap="wrap" style={{ marginTop: designToken.marginSM }}>
                <Text>Your zone</Text>
                <CopyableValue value={zoneName ?? domain} variant="inline" />
              </Flex>
            </Card>

            <Flex vertical gap={designToken.marginXXS}>
              <Input.Password
                aria-label="Cloudflare API token"
                placeholder="Paste your Cloudflare API token here"
                size="large"
                value={tokenValue}
                onChange={(event) => onTokenChange(event.target.value)}
                onPressEnter={onConnect}
                disabled={isLoading}
                status={FIELD_STATUS[fieldStatus.kind]}
                suffix={<FieldStatusIcon kind={fieldStatus.kind} />}
                // Never revealed: the eye toggle would put a live credential on
                // screen, and there is no reason to read it back.
                visibilityToggle={false}
              />
              {fieldStatus.message && (
                <Text style={{ color: fieldStatus.kind === 'error' ? designToken.colorError : designToken.colorWarning }}>
                  {fieldStatus.message}
                  {fieldStatus.offersRetry && (
                    <>
                      {' '}
                      <Button type="link" onClick={onRetry} style={{ padding: 0, height: 'auto' }}>
                        try again
                      </Button>
                      .
                    </>
                  )}
                </Text>
              )}
            </Flex>

            <Checkbox checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)}>
              {ACKNOWLEDGEMENT_TEXT}
            </Checkbox>

            {(problem === 'beta_closed' || betaClosed) && (
              <Alert
                type="info"
                showIcon
                role="alert"
                message={BETA_CLOSED_TITLE}
                description={`${BETA_CLOSED_REASON} Nothing was saved. Go back to integrate using the guides or Nexus.`}
              />
            )}

            {problem === 'flow_not_taking_token' && <Alert type="info" showIcon role="alert" {...flowNotTakingTokenCopy(domain)} />}

            {problem === 'request_failed' && <Alert type="error" showIcon role="alert" message={REQUEST_FAILED_COPY} />}

            {offerNexus && (
              <Alert
                type="info"
                showIcon
                message="Still stuck?"
                description={
                  // A Button, not a `Typography.Link`: a link with no `href` has no
                  // button role and no keyboard focus. Wrapped, not forwarded — see
                  // CloudflareEntryStep.
                  <Button
                    type="link"
                    onClick={openNexus}
                    style={{ padding: 0 }}
                  >
                    Ask Nexus
                  </Button>
                }
              />
            )}
          </Flex>

          {/* The DPO disclosure. It sits beside the field rather than behind a
              link, because it has to be read before the token is handed over. */}
          <Flex vertical gap={designToken.marginMD} flex={1} style={{ minWidth: designToken.screenXS }}>
            <Flex vertical>
              <Text strong>Why we need this token</Text>
              <Text>
                Prerender uses this token to set up the integration in your Cloudflare account. Nothing else. We&apos;ve
                pre-selected the permissions you need.
              </Text>
            </Flex>

            <Flex vertical>
              <Text strong>What we do with this token</Text>
              <Text>
                With this token, Prerender sets up your integration for you: it manages your DNS records, deploys a
                Cloudflare Worker, and configures routing so crawlers get your fully rendered pages.
              </Text>
            </Flex>

            <Flex vertical>
              <Text strong>Storage</Text>
              <Text>Your token is stored encrypted.</Text>
            </Flex>

            <Flex vertical>
              <Text strong>Scope</Text>
              <Text>
                Cloudflare tokens can cover every zone in your account by default. We recommend restricting this token
                to the one domain you&apos;re integrating.
              </Text>
            </Flex>

            <Flex vertical>
              <Text strong>Your responsibility</Text>
              <Text>
                You control this token. You are responsible for revoking and rotating it in Cloudflare. Prerender cannot
                revoke it for you.
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <Flex justify="space-between" align="center">
        {onGoBack ? (
          <Button type="link" icon={<LeftOutlined />} onClick={onGoBack}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Button
          type="primary"
          onClick={onConnect}
          loading={isLoading}
          // Disabled until the acknowledgement is given. The DPO review asks
          // for a recorded acknowledgement, and a control that works without
          // one would record nothing.
          disabled={!canConnect}
        >
          Connect Prerender
        </Button>
      </Flex>
    </Flex>
  );
};

export default CloudflareTokenStep;
